import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowUpDown,
  Building2,
  Check,
  CreditCard,
  FileText,
  MoreHorizontal,
  Plug,
  Search as SearchIcon,
  Shield,
  User,
  Users,
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useAuth } from '../contexts/AuthContext';
import { supabase as supabaseClient } from '../supabaseClient';
import { aiExperimentsApi, certificatesApi, getApiErrorMessage, pdfFirstTemplatesApi, settingsApi } from '../utils/api';
import {
  SettingsPayload,
  queryKeys,
  useChangePassword,
  useCancelInvite,
  useCertificateTemplatesData,
  useDisableMfa,
  useInviteMember,
  useMfaStatus,
  useRemoveMember,
  useResendInvite,
  useSettingsData,
  useTeamData,
  useUpdateMemberRole,
  useUpdateSettings,
} from '../hooks/useData';
import EngineerProfileEditor from '../components/EngineerProfileEditor';
import InviteMemberModal from '../components/InviteMemberModal';
import BillingPanel from '../components/Settings/BillingPanel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../contexts/ToastContext';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const TemplateSchemaHistory = ({ templateId }: { templateId: string }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['certificates', 'template-schemas', templateId],
    queryFn: async () => {
      const res = await certificatesApi.listSchemaSnapshotsByTemplate(templateId);
      const payload = res.data;
      if (payload?.success && payload?.data) return payload.data;
      return payload?.data || payload || [];
    },
    enabled: Boolean(String(templateId || '').trim()),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const rows: any[] = Array.isArray(data) ? data : [];

  const summarizeSchema = (schemaJson: any) => {
    const screens: any[] = Array.isArray(schemaJson?.screens) ? schemaJson.screens : [];
    let fields = 0;
    let contextFields = 0;
    for (const s of screens) {
      const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
      for (const sec of sections) {
        const fs: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
        fields += fs.length;
        for (const f of fs) {
          if (String(f?.context || '').trim()) contextFields += 1;
        }
      }
    }
    return { screens: screens.length, fields, contextFields };
  };

  return (
    <div className="mt-3 rounded-lg border border-[#E6E6E4] bg-[#FAFAF9] p-4">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Schema history</div>
      {isLoading ? <div className="mt-2 text-xs text-[#686E6C]">Loadingâ€¦</div> : null}
      {isError ? <div className="mt-2 text-xs text-red-700">Failed to load schema history.</div> : null}

      {!isLoading && !isError && rows.length === 0 ? (
        <div className="mt-2 text-xs text-[#686E6C]">No schema snapshots found for this template.</div>
      ) : null}

      {!isLoading && !isError && rows.length ? (
        <div className="mt-3 space-y-2">
          {rows
            .slice()
            .sort((a, b) => Number(a?.version ?? 0) - Number(b?.version ?? 0))
            .map((r: any) => {
              const v = Number(r?.version ?? 0);
              const createdAt = r?.createdAt ? new Date(r.createdAt) : null;
              const s = summarizeSchema(r?.schemaJson);
              return (
                <div key={String(r?.id || v)} className="rounded-lg border border-[#E6E6E4] bg-white p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-medium text-[#181818]">Version {Number.isFinite(v) && v > 0 ? v : 'â€”'}</div>
                    <div className="text-xs text-[#686E6C]">{createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt.toLocaleString() : 'â€”'}</div>
                  </div>
                  <div className="mt-2 text-xs text-[#686E6C]">
                    {s.screens} screens
                    <span className="mx-2">â€¢</span>
                    {s.fields} fields
                    <span className="mx-2">â€¢</span>
                    {s.contextFields} with context
                  </div>
                </div>
              );
            })}
        </div>
      ) : null}
    </div>
  );
};

type SettingsTab =
  | 'general'
  | 'company'
  | 'team'
  | 'privacy'
  | 'billing'
  | 'integrations'
  | 'documents';

const TABS: Array<{ key: SettingsTab; label: string }> = [
  { key: 'general', label: 'General Settings' },
  { key: 'company', label: 'Workspace' },
  { key: 'team', label: 'Team Members' },
  { key: 'privacy', label: 'Privacy & Security' },
  { key: 'billing', label: 'Billing' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'documents', label: 'Documents' },
];

const SettingsCard = ({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="rounded-xl border border-[#E6E6E4] bg-white shadow-sm">
      <div className="flex items-start gap-3 px-6 py-5">
        <div className="mt-0.5 text-[#686E6C]">{icon}</div>
        <div className="min-w-0">
          <div className="text-sm font-semibold leading-5 text-[#181818]">{title}</div>
          <div className="mt-1 text-sm leading-5 text-[#686E6C]">{subtitle}</div>
        </div>
      </div>
      <div className="border-t border-[#E6E6E4]" />
      <div className="px-6 py-6">{children}</div>
    </div>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="space-y-2">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
};

const FieldRow = ({ children }: { children: React.ReactNode }) => {
  return <div className="grid grid-cols-1 gap-5 md:grid-cols-2">{children}</div>;
};

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [headerSearch, setHeaderSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [billingPeriod, setBillingPeriod] = useState<'Monthly' | 'Annual'>('Monthly');
  const [historyTemplateId, setHistoryTemplateId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { data: settingsData, isLoading } = useSettingsData();
  const { data: certificateTemplates = [], isLoading: certificateTemplatesLoading } = useCertificateTemplatesData();
  const updateSettings = useUpdateSettings();
  const changePassword = useChangePassword();
  const { data: teamData, isLoading: teamLoading } = useTeamData();
  const inviteMember = useInviteMember();
  const updateMemberRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();
  const cancelInvite = useCancelInvite();
  const resendInvite = useResendInvite();
  const { data: mfaData, refetch: refetchMfa } = useMfaStatus();
  const disableMfa = useDisableMfa();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [engineerEditorTarget, setEngineerEditorTarget] = useState<any | null>(null);
  const [mfaEnrolling, setMfaEnrolling] = useState(false);
  const [mfaQr, setMfaQr] = useState<string | null>(null);
  const [mfaSecret, setMfaSecret] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);

  const [pdfFirstCertificateTypeCode, setPdfFirstCertificateTypeCode] = useState<string>('CP17');
  const [pdfFirstCertificateTypeLabel, setPdfFirstCertificateTypeLabel] = useState<string>('');
  const [pdfFirstCertificatePrefix, setPdfFirstCertificatePrefix] = useState<string>('GS17');
  const [pdfFirstFile, setPdfFirstFile] = useState<File | null>(null);
  const [pdfFirstResult, setPdfFirstResult] = useState<any>(null);
  const [pdfFirstExtraction, setPdfFirstExtraction] = useState<any>(null);
  const [pdfFirstSchemaDraft, setPdfFirstSchemaDraft] = useState<any>(null);
  const [pdfFirstMoveTargets, setPdfFirstMoveTargets] = useState<Record<string, { sectionId: string; screenIndex: number }>>(
    {},
  );
  const [pdfFirstSelectedPdfFieldName, setPdfFirstSelectedPdfFieldName] = useState<string>('');
  const [pdfFirstPreviewPageIndex, setPdfFirstPreviewPageIndex] = useState<number>(0);
  const [pdfFirstDrawerOpen, setPdfFirstDrawerOpen] = useState<boolean>(false);
  const [pdfFirstDrawerTemplate, setPdfFirstDrawerTemplate] = useState<any>(null);
  const [pdfFirstDrawerSchemaDraft, setPdfFirstDrawerSchemaDraft] = useState<any>(null);
  const [pdfFirstDrawerBaselineJson, setPdfFirstDrawerBaselineJson] = useState<string>('');
  const [pdfFirstDrawerMoveTargets, setPdfFirstDrawerMoveTargets] = useState<Record<string, string>>({});

  const resultsSplitRef = useRef<HTMLDivElement | null>(null);
  const pdfPreviewRef = useRef<HTMLDivElement | null>(null);
  const [pdfPreviewWidth, setPdfPreviewWidth] = useState<number>(320);
  const fieldItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [pdfLinkLine, setPdfLinkLine] = useState<null | { x1: number; y1: number; x2: number; y2: number }>(null);

  const profilePhotoInputRef = useRef<HTMLInputElement | null>(null);
  const companyLogoInputRef = useRef<HTMLInputElement | null>(null);
  const invoiceTemplateInputRef = useRef<HTMLInputElement | null>(null);
  const certificateTemplateInputRef = useRef<HTMLInputElement | null>(null);
  const termsTemplateInputRef = useRef<HTMLInputElement | null>(null);

  const activeTab = useMemo<SettingsTab>(() => {
    const raw = (searchParams.get('tab') || '').toLowerCase();
    const allowed = new Set<SettingsTab>([
      'general',
      'company',
      'team',
      'privacy',
      'billing',
      'integrations',
      'documents',
    ]);
    if (allowed.has(raw as SettingsTab)) return raw as SettingsTab;
    return 'general';
  }, [searchParams]);

  const setTab = (tab: SettingsTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const displayName = user?.name || user?.email || 'User';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const [firstName, lastName] = useMemo(() => {
    const parts = (user?.name || '').trim().split(' ').filter(Boolean);
    if (parts.length === 0) return ['', ''];
    if (parts.length === 1) return [parts[0], ''];
    return [parts[0], parts.slice(1).join(' ')];
  }, [user?.name]);

  const [formState, setFormState] = useState<SettingsPayload>({
    user: {
      firstName,
      lastName,
      phone: (user as any)?.phone || '',
      address: (user as any)?.address || '',
      gasSafeNumber: (user as any)?.gasSafeNumber || '',
      dateOfBirth: (user as any)?.dob || '',
    },
    company: {
      name: user?.companyName || '',
      workingHoursStart: '',
      workingHoursEnd: '',
    },
    settings: {
      paymentTermsDays: 30,
      notificationEmail: true,
      notificationSms: false,
      autoReminders: true,
      invoicePrefix: 'INV-',
      invoiceNextNumber: 1,
      certificatePrefix: 'CERT-',
      certificateNextNumber: 1,
    },
  });

  const updateExistingTemplate = useMutation({
    mutationFn: async () => {
      const id = String(pdfFirstDrawerTemplate?.id || '').trim();
      if (!id) throw new Error('Missing template id');
      const schemaJson = pdfFirstDrawerSchemaDraft;
      if (!schemaJson || typeof schemaJson !== 'object') throw new Error('Missing schema');
      const res = await certificatesApi.updateTemplate(id, { schemaJson });
      return res?.data?.data || res?.data || res;
    },
    onSuccess: async (updated: any) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.certificateTemplates });
      setPdfFirstDrawerTemplate(updated);
      const nextSchema = updated?.schemaJson ? JSON.parse(JSON.stringify(updated.schemaJson)) : null;
      setPdfFirstDrawerSchemaDraft(nextSchema);
      setPdfFirstDrawerBaselineJson(JSON.stringify(nextSchema || {}));
      showToast('Template updated', 'success');
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message || e?.message || 'Failed to update template';
      showToast(String(msg), 'error');
    },
  });

  const pdfFirstDrawerDirty = useMemo(() => {
    const base = String(pdfFirstDrawerBaselineJson || '').trim();
    if (!base) return false;
    try {
      const cur = JSON.stringify(pdfFirstDrawerSchemaDraft || {});
      return cur !== base;
    } catch {
      return false;
    }
  }, [pdfFirstDrawerSchemaDraft, pdfFirstDrawerBaselineJson]);

  const updateDrawerScreenTitle = (screenIdx: number, title: string) => {
    setPdfFirstDrawerSchemaDraft((prev: any) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const screens: any[] = Array.isArray(next?.screens) ? next.screens : [];
      if (!screens[screenIdx]) return prev;
      screens[screenIdx] = { ...screens[screenIdx], title };
      next.screens = screens;
      return next;
    });
  };

  const updateDrawerSectionTitle = (screenIdx: number, sectionIdx: number, title: string) => {
    setPdfFirstDrawerSchemaDraft((prev: any) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const screens: any[] = Array.isArray(next?.screens) ? next.screens : [];
      const s = screens[screenIdx];
      if (!s) return prev;
      const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
      if (!sections[sectionIdx]) return prev;
      sections[sectionIdx] = { ...sections[sectionIdx], title };
      screens[screenIdx] = { ...s, sections };
      next.screens = screens;
      return next;
    });
  };

  const updateDrawerFieldLabel = (screenIdx: number, sectionIdx: number, fieldIdx: number, label: string) => {
    setPdfFirstDrawerSchemaDraft((prev: any) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const screens: any[] = Array.isArray(next?.screens) ? next.screens : [];
      const s = screens[screenIdx];
      if (!s) return prev;
      const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
      const sec = sections[sectionIdx];
      if (!sec) return prev;
      const fields: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
      if (!fields[fieldIdx]) return prev;
      fields[fieldIdx] = { ...fields[fieldIdx], label };
      sections[sectionIdx] = { ...sec, fields };
      screens[screenIdx] = { ...s, sections };
      next.screens = screens;
      return next;
    });
  };

  const moveArrayItem = <T,>(arr: T[], from: number, to: number): T[] => {
    const a = Array.isArray(arr) ? [...arr] : [];
    if (from < 0 || from >= a.length) return a;
    if (to < 0 || to >= a.length) return a;
    if (from === to) return a;
    const [it] = a.splice(from, 1);
    a.splice(to, 0, it);
    return a;
  };

  const addDrawerScreen = () => {
    setPdfFirstDrawerSchemaDraft((prev: any) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const screens: any[] = Array.isArray(next?.screens) ? next.screens : [];
      const key = `custom_${Date.now()}`;
      screens.push({
        key,
        title: 'New screen',
        sections: [{ key: `${key}_section_1`, title: 'New section', fields: [] }],
      });
      next.screens = screens;
      return next;
    });
  };

  const removeDrawerScreen = (screenIdx: number) => {
    setPdfFirstDrawerSchemaDraft((prev: any) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const screens: any[] = Array.isArray(next?.screens) ? next.screens : [];
      if (!screens[screenIdx]) return prev;
      screens.splice(screenIdx, 1);
      next.screens = screens;
      return next;
    });
  };

  const addDrawerSection = (screenIdx: number) => {
    setPdfFirstDrawerSchemaDraft((prev: any) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const screens: any[] = Array.isArray(next?.screens) ? next.screens : [];
      const s = screens[screenIdx];
      if (!s) return prev;
      const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
      const key = `${String(s?.key || `screen_${screenIdx}`)}_section_${Date.now()}`;
      sections.push({ key, title: 'New section', fields: [] });
      screens[screenIdx] = { ...s, sections };
      next.screens = screens;
      return next;
    });
  };

  const removeDrawerSection = (screenIdx: number, sectionIdx: number) => {
    setPdfFirstDrawerSchemaDraft((prev: any) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const screens: any[] = Array.isArray(next?.screens) ? next.screens : [];
      const s = screens[screenIdx];
      if (!s) return prev;
      const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
      if (!sections[sectionIdx]) return prev;
      sections.splice(sectionIdx, 1);
      screens[screenIdx] = { ...s, sections };
      next.screens = screens;
      return next;
    });
  };

  const reorderDrawerScreens = (from: number, to: number) => {
    setPdfFirstDrawerSchemaDraft((prev: any) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const screens: any[] = Array.isArray(next?.screens) ? next.screens : [];
      next.screens = moveArrayItem(screens, from, to);
      return next;
    });
  };

  const reorderDrawerSections = (screenIdx: number, from: number, to: number) => {
    setPdfFirstDrawerSchemaDraft((prev: any) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const screens: any[] = Array.isArray(next?.screens) ? next.screens : [];
      const s = screens[screenIdx];
      if (!s) return prev;
      const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
      screens[screenIdx] = { ...s, sections: moveArrayItem(sections, from, to) };
      next.screens = screens;
      return next;
    });
  };

  const reorderDrawerFields = (screenIdx: number, sectionIdx: number, from: number, to: number) => {
    setPdfFirstDrawerSchemaDraft((prev: any) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const screens: any[] = Array.isArray(next?.screens) ? next.screens : [];
      const s = screens[screenIdx];
      if (!s) return prev;
      const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
      const sec = sections[sectionIdx];
      if (!sec) return prev;
      const fields: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
      sections[sectionIdx] = { ...sec, fields: moveArrayItem(fields, from, to) };
      screens[screenIdx] = { ...s, sections };
      next.screens = screens;
      return next;
    });
  };

  const moveDrawerFieldTo = (args: {
    fromScreenIdx: number;
    fromSectionIdx: number;
    fromFieldIdx: number;
    toScreenIdx: number;
    toSectionIdx: number;
  }) => {
    setPdfFirstDrawerSchemaDraft((prev: any) => {
      const next = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const screens: any[] = Array.isArray(next?.screens) ? next.screens : [];
      const fromS = screens[args.fromScreenIdx];
      const toS = screens[args.toScreenIdx];
      if (!fromS || !toS) return prev;
      const fromSections: any[] = Array.isArray(fromS?.sections) ? fromS.sections : [];
      const toSections: any[] = Array.isArray(toS?.sections) ? toS.sections : [];
      const fromSec = fromSections[args.fromSectionIdx];
      const toSec = toSections[args.toSectionIdx];
      if (!fromSec || !toSec) return prev;
      const fromFields: any[] = Array.isArray(fromSec?.fields) ? fromSec.fields : [];
      const toFields: any[] = Array.isArray(toSec?.fields) ? toSec.fields : [];
      const field = fromFields[args.fromFieldIdx];
      if (!field) return prev;

      fromFields.splice(args.fromFieldIdx, 1);
      toFields.push(field);

      fromSections[args.fromSectionIdx] = { ...fromSec, fields: fromFields };
      toSections[args.toSectionIdx] = { ...toSec, fields: toFields };

      screens[args.fromScreenIdx] = { ...fromS, sections: fromSections };
      screens[args.toScreenIdx] = { ...toS, sections: toSections };
      next.screens = screens;
      return next;
    });
  };

  const drawerMoveOptions = useMemo(() => {
    const schema = pdfFirstDrawerSchemaDraft || pdfFirstDrawerTemplate?.schemaJson;
    const screens: any[] = Array.isArray(schema?.screens) ? schema.screens : [];
    const opts: Array<{ value: string; label: string; screenIdx: number; sectionIdx: number }> = [];
    for (let sIdx = 0; sIdx < screens.length; sIdx += 1) {
      const s = screens[sIdx];
      const sTitle = String(s?.title || s?.key || `Screen ${sIdx + 1}`);
      const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
      for (let secIdx = 0; secIdx < sections.length; secIdx += 1) {
        const sec = sections[secIdx];
        const secTitle = String(sec?.title || sec?.key || `Section ${secIdx + 1}`);
        opts.push({
          value: `${sIdx}:${secIdx}`,
          label: `${sTitle} / ${secTitle}`,
          screenIdx: sIdx,
          sectionIdx: secIdx,
        });
      }
    }
    return opts;
  }, [pdfFirstDrawerSchemaDraft, pdfFirstDrawerTemplate]);

  const [baselineState, setBaselineState] = useState<SettingsPayload>(formState);

  const resolveCertificatePrefix = (certificateTypeCode: string) => {
    const code = String(certificateTypeCode || '').trim().toUpperCase();
    const m = code.match(/^CP(\d+)$/);
    if (m && m[1]) return `GS${m[1]}`;
    return 'GS';
  };

  useEffect(() => {
    const onResize = () => {
      const w = pdfPreviewRef.current?.clientWidth;
      if (typeof w === 'number' && Number.isFinite(w) && w > 200) setPdfPreviewWidth(w);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const pdfFirstCertificateTypeName = useMemo(() => {
    if (pdfFirstCertificateTypeCode === 'OTHER') {
      const label = String(pdfFirstCertificateTypeLabel || '').trim();
      return label;
    }
    return String(pdfFirstCertificateTypeCode || '').trim();
  }, [pdfFirstCertificateTypeCode, pdfFirstCertificateTypeLabel]);

  const pdfFirstCertificateTypeCodeBackend = useMemo(() => {
    return pdfFirstCertificateTypeCode === 'OTHER' ? 'USER_DECLARED' : pdfFirstCertificateTypeCode;
  }, [pdfFirstCertificateTypeCode]);

  const existingTemplateForSelectedType = useMemo(() => {
    const arr: any[] = Array.isArray(certificateTemplates) ? (certificateTemplates as any[]) : [];
    const typeName = String(pdfFirstCertificateTypeName || '').trim();
    if (!typeName) return null;
    return (
      arr.find((t: any) => String(t?.certificateTypeName || '').trim() === typeName) ||
      arr.find((t: any) => String(t?.certificateTypeName || '').trim() === String(pdfFirstCertificateTypeCode || '').trim()) ||
      null
    );
  }, [certificateTemplates, pdfFirstCertificateTypeName, pdfFirstCertificateTypeCode]);

  const canUploadNewForSelectedType = useMemo(() => {
    const typeName = String(pdfFirstCertificateTypeName || '').trim();
    if (!typeName) return false;
    if (pdfFirstCertificateTypeCode === 'OTHER' && !String(pdfFirstCertificateTypeLabel || '').trim()) return false;
    return !existingTemplateForSelectedType;
  }, [existingTemplateForSelectedType, pdfFirstCertificateTypeName, pdfFirstCertificateTypeCode, pdfFirstCertificateTypeLabel]);

  useEffect(() => {
    const next = resolveCertificatePrefix(pdfFirstCertificateTypeCode);
    setPdfFirstCertificatePrefix(next);
  }, [pdfFirstCertificateTypeCode]);

  const normalizeExtractedField = (f: any) => {
    const pdfFieldName = String(f?.pdfFieldName || f?.pdf_field_name || '').trim();
    const fieldType = String(f?.fieldType || f?.field_type || 'text').trim() || 'text';
    const displayLabelSuggested =
      String(f?.displayLabelSuggested || f?.display_label_suggested || '').trim() || undefined;
    return { pdfFieldName, fieldType, displayLabelSuggested };
  };

  const pdfFirstSchemaToRender = useMemo(() => {
    return pdfFirstSchemaDraft || (pdfFirstResult as any)?.schemaJson || null;
  }, [pdfFirstSchemaDraft, pdfFirstResult]);

  const pdfFirstSections = useMemo(() => {
    return Array.isArray((pdfFirstSchemaToRender as any)?.sections) ? (pdfFirstSchemaToRender as any).sections : [];
  }, [pdfFirstSchemaToRender]);

  const pdfFirstExtractedFields = useMemo(() => {
    const raw: any[] = Array.isArray((pdfFirstExtraction as any)?.fields) ? (pdfFirstExtraction as any).fields : [];
    return raw
      .map(normalizeExtractedField)
      .filter((x) => Boolean(String(x?.pdfFieldName || '').trim()));
  }, [pdfFirstExtraction]);

  const pdfPreviewPages = useMemo(() => {
    const arr: any[] = Array.isArray((pdfFirstExtraction as any)?.pages) ? (pdfFirstExtraction as any).pages : [];
    return arr;
  }, [pdfFirstExtraction]);

  const pdfPreviewFieldsWithGeom = useMemo(() => {
    const raw: any[] = Array.isArray((pdfFirstExtraction as any)?.fields) ? (pdfFirstExtraction as any).fields : [];
    return raw
      .map((f: any) => ({
        pdfFieldName: String(f?.pdfFieldName || f?.pdf_field_name || '').trim(),
        pageIndex: typeof f?.pageIndex === 'number' ? f.pageIndex : (typeof f?.page_index === 'number' ? f.page_index : undefined),
        x: typeof f?.x === 'number' ? f.x : undefined,
        y: typeof f?.y === 'number' ? f.y : undefined,
        width: typeof f?.width === 'number' ? f.width : undefined,
        height: typeof f?.height === 'number' ? f.height : undefined,
      }))
      .filter((x: any) => Boolean(x.pdfFieldName));
  }, [pdfFirstExtraction]);

  const geomByPdfFieldName = useMemo(() => {
    const map = new Map<string, any>();
    for (const f of pdfPreviewFieldsWithGeom) {
      if (f?.pdfFieldName && !map.has(f.pdfFieldName)) map.set(f.pdfFieldName, f);
    }
    return map;
  }, [pdfPreviewFieldsWithGeom]);

  const selectedGeom = useMemo(() => {
    const name = String(pdfFirstSelectedPdfFieldName || '').trim();
    if (!name) return null;
    return geomByPdfFieldName.get(name) || null;
  }, [pdfFirstSelectedPdfFieldName, geomByPdfFieldName]);

  useEffect(() => {
    const idx = typeof (selectedGeom as any)?.pageIndex === 'number' ? Number((selectedGeom as any).pageIndex) : NaN;
    if (Number.isFinite(idx) && idx >= 0) setPdfFirstPreviewPageIndex(idx);
  }, [selectedGeom]);

  useEffect(() => {
    const container = resultsSplitRef.current;
    if (!container) {
      setPdfLinkLine(null);
      return;
    }
    const name = String(pdfFirstSelectedPdfFieldName || '').trim();
    if (!name) {
      setPdfLinkLine(null);
      return;
    }
    const leftEl = fieldItemRefs.current[name];
    const geom = selectedGeom;
    const pageIdx = typeof geom?.pageIndex === 'number' ? geom.pageIndex : undefined;
    const pageMeta = typeof pageIdx === 'number' ? pdfPreviewPages[pageIdx] : null;
    if (!leftEl || !geom || !pageMeta || !Number.isFinite(pageMeta?.width) || !Number.isFinite(pageMeta?.height)) {
      setPdfLinkLine(null);
      return;
    }

    const pdfPane = pdfPreviewRef.current;
    const pdfRect = pdfPane?.getBoundingClientRect();
    if (!pdfRect) {
      setPdfLinkLine(null);
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const leftRect = leftEl.getBoundingClientRect();
    const x1 = leftRect.right - containerRect.left;
    const y1 = leftRect.top - containerRect.top + leftRect.height / 2;

    const scale = pdfPreviewWidth / Number(pageMeta.width);
    const x = typeof geom?.x === 'number' ? geom.x : NaN;
    const y = typeof geom?.y === 'number' ? geom.y : NaN;
    const w = typeof geom?.width === 'number' ? geom.width : NaN;
    const h = typeof geom?.height === 'number' ? geom.height : NaN;
    if (![x, y, w, h].every((n) => typeof n === 'number' && Number.isFinite(n))) {
      setPdfLinkLine(null);
      return;
    }

    const topPx = (Number(pageMeta.height) - y - h) * scale;
    const leftPx = x * scale;
    const cx = pdfRect.left - containerRect.left + leftPx + (w * scale) / 2;
    const cy = pdfRect.top - containerRect.top + topPx + (h * scale) / 2;

    setPdfLinkLine({ x1, y1, x2: cx, y2: cy });
  }, [pdfFirstSelectedPdfFieldName, selectedGeom, pdfPreviewPages, pdfPreviewWidth]);

  const pdfFirstMissingFields = useMemo(() => {
    const present = new Set<string>();
    for (const sec of pdfFirstSections) {
      const screens: any[] = Array.isArray((sec as any)?.screens) ? (sec as any).screens : [];
      for (const scr of screens) {
        const fields: any[] = Array.isArray((scr as any)?.fields) ? (scr as any).fields : [];
        for (const f of fields) {
          const n = String((f as any)?.pdfFieldName || '').trim();
          if (n) present.add(n);
        }
      }
    }

    return pdfFirstExtractedFields.filter((f) => !present.has(String(f.pdfFieldName)));
  }, [pdfFirstExtractedFields, pdfFirstSections]);

  const getDraftSchema = () => {
    const base = pdfFirstSchemaDraft || (pdfFirstResult as any)?.schemaJson;
    if (!base || typeof base !== 'object') return null;
    return JSON.parse(JSON.stringify(base));
  };

  const ensureUnassigned = (schema: any) => {
    if (!schema || typeof schema !== 'object') return schema;
    if (!Array.isArray(schema.sections)) schema.sections = [];
    let sec = schema.sections.find((s: any) => String(s?.id || '').trim() === 'unassigned');
    if (!sec) {
      sec = {
        id: 'unassigned',
        title: 'Unassigned',
        repeatable: false,
        screens: [
          {
            title: 'Unassigned',
            description: 'Fields not yet assigned to the correct section/screen.',
            fields: [],
          },
        ],
      };
      schema.sections.push(sec);
    }
    if (!Array.isArray(sec.screens) || sec.screens.length === 0) {
      sec.screens = [
        {
          title: 'Unassigned',
          description: 'Fields not yet assigned to the correct section/screen.',
          fields: [],
        },
      ];
    }
    if (!Array.isArray(sec.screens[0].fields)) sec.screens[0].fields = [];
    return schema;
  };

  const addToUnassigned = (f: { pdfFieldName: string; fieldType: string; displayLabelSuggested?: string }) => {
    const next = getDraftSchema();
    if (!next) return;
    ensureUnassigned(next);

    const present = new Set<string>();
    for (const sec of next.sections || []) {
      const screens: any[] = Array.isArray(sec?.screens) ? sec.screens : [];
      for (const scr of screens) {
        const fields: any[] = Array.isArray(scr?.fields) ? scr.fields : [];
        for (const ff of fields) {
          const n = String(ff?.pdfFieldName || '').trim();
          if (n) present.add(n);
        }
      }
    }

    const pdfFieldName = String(f?.pdfFieldName || '').trim();
    if (!pdfFieldName || present.has(pdfFieldName)) return;

    const sec = next.sections.find((s: any) => String(s?.id || '').trim() === 'unassigned');
    const scr = sec?.screens?.[0];
    if (!scr) return;

    const label = String(f?.displayLabelSuggested || pdfFieldName).trim() || pdfFieldName;
    scr.fields.push({
      pdfFieldName,
      label,
      fieldType: String(f?.fieldType || 'text').trim() || 'text',
      context: '',
    });

    setPdfFirstSchemaDraft(next);
  };

  const addAllMissingToUnassigned = () => {
    for (const f of pdfFirstMissingFields) addToUnassigned(f);
  };

  const pdfFirstUnassignedFields = useMemo(() => {
    const un = (pdfFirstSections as any[]).find((s: any) => String(s?.id || '').trim() === 'unassigned');
    if (!un) return [];
    const scr = Array.isArray(un?.screens) ? un.screens[0] : null;
    const fields: any[] = Array.isArray(scr?.fields) ? scr.fields : [];
    return fields;
  }, [pdfFirstSections]);

  const pdfFirstAssignableSections = useMemo(() => {
    return (pdfFirstSections as any[]).filter(
      (s: any) => String(s?.id || '').trim() && String(s?.id || '').trim() !== 'unassigned',
    );
  }, [pdfFirstSections]);

  const moveFromUnassigned = (pdfFieldNameRaw: string) => {
    const pdfFieldName = String(pdfFieldNameRaw || '').trim();
    if (!pdfFieldName) return;

    const next = getDraftSchema();
    if (!next) return;
    ensureUnassigned(next);

    const target = pdfFirstMoveTargets[pdfFieldName];
    const targetSectionId = String(target?.sectionId || '').trim();
    const targetScreenIndex = Number.isFinite(Number(target?.screenIndex)) ? Number(target?.screenIndex) : 0;
    if (!targetSectionId) return;

    const unSec = next.sections.find((s: any) => String(s?.id || '').trim() === 'unassigned');
    const unScr = unSec?.screens?.[0];
    if (!unScr || !Array.isArray(unScr.fields)) return;

    const moving = unScr.fields.find((f: any) => String(f?.pdfFieldName || '').trim() === pdfFieldName);
    if (!moving) return;
    unScr.fields = unScr.fields.filter((f: any) => String(f?.pdfFieldName || '').trim() !== pdfFieldName);

    const tSec = next.sections.find((s: any) => String(s?.id || '').trim() === targetSectionId);
    if (!tSec) return;
    if (!Array.isArray(tSec.screens) || tSec.screens.length === 0) return;
    const tScr = tSec.screens[Math.max(0, Math.min(tSec.screens.length - 1, targetScreenIndex))];
    if (!tScr) return;
    if (!Array.isArray(tScr.fields)) tScr.fields = [];
    tScr.fields.push(moving);

    setPdfFirstSchemaDraft(next);
  };

  const runPdfFirstExperiment = useMutation({
    mutationFn: async (args: { file: File; certificateTypeCode: string; certificateTypeLabel?: string }) => {
      const typeName = [args.certificateTypeCode, args.certificateTypeLabel].filter(Boolean).join(' ').trim();
      const tmplLabel = 'MASTER';
      const extracted = await certificatesApi.extractTemplateFields({
        file: args.file,
        certificatePrefix: String(pdfFirstCertificatePrefix || 'GS').trim() || 'GS',
        certificateTypeName: typeName || args.certificateTypeCode,
        templateLabel: tmplLabel,
      });
      const extractedPayload = extracted?.data;
      const extractedData = extractedPayload?.data || extractedPayload;
      setPdfFirstExtraction(extractedData);

      return aiExperimentsApi.pdfFirstInferFromExtraction({
        certificateTypeCode: args.certificateTypeCode,
        certificateTypeLabel: args.certificateTypeLabel,
        pdfStoragePath: String(extractedData?.pdfStoragePath || '').trim(),
        fields: Array.isArray(extractedData?.fields) ? extractedData.fields : [],
        pages: Array.isArray(extractedData?.pages) ? extractedData.pages : [],
      });
    },
    onSuccess: (res: any) => {
      const payload = res?.data;
      const data = payload?.data || payload;
      setPdfFirstResult(data);
      setPdfFirstSchemaDraft(data?.schemaJson || null);
    },
    onError: (e: unknown) => {
      showToast(getApiErrorMessage(e), 'error');
    },
  });

  const loadLatestPdfFirstExperiment = useMutation({
    mutationFn: (certificateTypeCode: string) => aiExperimentsApi.pdfFirstLatestByType(certificateTypeCode),
    onSuccess: (res: any) => {
      const payload = res?.data;
      const data = payload?.data || payload;
      setPdfFirstResult(data);
      setPdfFirstSchemaDraft(data?.schemaJson || null);
    },
    onError: (e: any) => {
      showToast(String(e?.message || 'Failed to load latest result'), 'error');
    },
  });

  const savePdfFirstAsTemplate = useMutation({
    mutationFn: async () => {
      const schemaJson = pdfFirstSchemaDraft || (pdfFirstResult as any)?.schemaJson;
      const extractedFieldsRaw = Array.isArray((pdfFirstExtraction as any)?.fields) ? (pdfFirstExtraction as any).fields : [];
      const pdfStoragePath = String((pdfFirstExtraction as any)?.pdfStoragePath || '').trim();

      if (!schemaJson || typeof schemaJson !== 'object') throw new Error('Missing schemaJson');
      if (!pdfStoragePath) throw new Error('Missing pdfStoragePath');
      if (!extractedFieldsRaw.length) throw new Error('Missing extracted fields');

      const tmplLabel = 'MASTER';

      return pdfFirstTemplatesApi.save({
        certificatePrefix: String(pdfFirstCertificatePrefix || 'GS').trim() || 'GS',
        certificateTypeCode: pdfFirstCertificateTypeCodeBackend,
        certificateTypeLabel: String(pdfFirstCertificateTypeLabel || '').trim() || undefined,
        templateLabel: tmplLabel,
        pdfStoragePath,
        pdfFirstSchemaJson: schemaJson,
        extractedFields: extractedFieldsRaw
          .map((f: any) => ({
            pdfFieldName: String(f?.pdfFieldName || f?.pdf_field_name || '').trim(),
            fieldType: String(f?.fieldType || f?.field_type || 'text').trim() || 'text',
            displayLabelSuggested: String(f?.displayLabelSuggested || f?.display_label_suggested || '').trim() || undefined,
          }))
          .filter((x: any) => Boolean(String(x?.pdfFieldName || '').trim())),
        lockIfValid: true,
      });
    },
    onSuccess: () => {
      showToast('Template saved', 'success');
    },
    onError: (e: any) => {
      showToast(String(e?.message || 'Failed to save template'), 'error');
    },
  });

  const profilePhotoUrl = (settingsData as any)?.user?.profilePhotoUrl as string | undefined;
  const companyLogoUrl = (settingsData as any)?.company?.logo as string | undefined;
  const invoiceTemplateUrl = (settingsData as any)?.settings?.invoiceTemplateUrl as string | undefined;
  const certificateTemplateUrl = (settingsData as any)?.settings?.certificateTemplateUrl as string | undefined;
  const termsTemplateUrl = (settingsData as any)?.settings?.termsTemplateUrl as string | undefined;

  const toDateInputValue = (value?: string | Date | null) => {
    if (!value) return '';
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) return '';
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    if (!settingsData) return;

    const apiUser = (settingsData as any).user;
    const apiCompany = (settingsData as any).company;
    const apiSettings = (settingsData as any).settings;

    const parsedName = String(apiUser?.name || '').trim().split(' ').filter(Boolean);
    const nextFirstName = parsedName.length > 0 ? parsedName[0] : '';
    const nextLastName = parsedName.length > 1 ? parsedName.slice(1).join(' ') : '';

    const next: SettingsPayload = {
      user: {
        firstName: nextFirstName,
        lastName: nextLastName,
        phone: apiUser?.phone ?? '',
        address: apiUser?.address ?? '',
        gasSafeNumber: apiUser?.gasSafeNumber ?? '',
        dateOfBirth: toDateInputValue(apiUser?.dateOfBirth ?? null),
      },
      company: {
        name: apiCompany?.name ?? '',
        registrationNumber: apiCompany?.registrationNumber ?? '',
        vatNumber: apiCompany?.vatNumber ?? '',
        gasRegistrationNumber: apiCompany?.gasRegistrationNumber ?? '',
        address: apiCompany?.address ?? '',
        phone: apiCompany?.phone ?? '',
        businessEmail: apiCompany?.businessEmail ?? '',
        workingHoursStart: apiCompany?.workingHoursStart ?? '',
        workingHoursEnd: apiCompany?.workingHoursEnd ?? '',
        vatRate: apiCompany?.vatRate ?? undefined,
      },
      settings: {
        bankSortCode: apiSettings?.bankSortCode ?? '',
        bankAccountNumber: apiSettings?.bankAccountNumber ?? '',
        paymentTermsDays: apiSettings?.paymentTermsDays ?? 30,
        invoiceFooter: apiSettings?.invoiceFooter ?? '',
        invoicePrefix: apiSettings?.invoicePrefix ?? 'INV-',
        invoiceNextNumber: apiSettings?.invoiceNextNumber ?? 1,
        certificatePrefix: apiSettings?.certificatePrefix ?? 'CERT-',
        certificateNextNumber: apiSettings?.certificateNextNumber ?? 1,
        notificationEmail: apiSettings?.notificationEmail ?? true,
        notificationSms: apiSettings?.notificationSms ?? false,
        autoReminders: apiSettings?.autoReminders ?? true,
      },
    };

    setFormState(next);
    setBaselineState(next);
  }, [settingsData]);

  const uploadProfilePhoto = useMutation({
    mutationFn: (file: File) => settingsApi.uploadProfilePhoto(file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.settings });
      showToast('Profile photo updated', 'success');
    },
    onError: (error: any) => {
      showToast(error?.message || 'Failed to upload profile photo', 'error');
    },
  });

  const uploadCompanyLogo = useMutation({
    mutationFn: (file: File) => settingsApi.uploadCompanyLogo(file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.settings });
      showToast('Company logo updated', 'success');
    },
    onError: (error: any) => {
      showToast(error?.message || 'Failed to upload company logo', 'error');
    },
  });

  const uploadInvoiceTemplate = useMutation({
    mutationFn: (file: File) => settingsApi.uploadInvoiceTemplate(file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.settings });
      showToast('Invoice template updated', 'success');
    },
    onError: (error: any) => {
      showToast(error?.message || 'Failed to upload invoice template', 'error');
    },
  });

  const uploadCertificateTemplate = useMutation({
    mutationFn: (file: File) => settingsApi.uploadCertificateTemplate(file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.settings });
      showToast('Certificate template updated', 'success');
    },
    onError: (error: any) => {
      showToast(error?.message || 'Failed to upload certificate template', 'error');
    },
  });

  const uploadTermsTemplate = useMutation({
    mutationFn: (file: File) => settingsApi.uploadTermsTemplate(file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.settings });
      showToast('Terms template updated', 'success');
    },
    onError: (error: any) => {
      showToast(error?.message || 'Failed to upload terms template', 'error');
    },
  });

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync(formState);
      setBaselineState(formState);
      showToast('Settings saved', 'success');
      await refreshUser();
      await queryClient.invalidateQueries({ queryKey: queryKeys.settings });
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Failed to save settings';
      showToast(msg, 'error');
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      showToast('Please fill all password fields', 'error');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }
    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      showToast('Password updated', 'success');
    } catch (e: any) {
      showToast(e?.message || 'Failed to update password', 'error');
    }
  };

  const handleCancel = () => {
    setFormState(baselineState);
  };

  const handleResendInvite = async (inviteId: string) => {
    try {
      const res: any = await resendInvite.mutateAsync(inviteId);
      const data = res?.data || res;
      const delivered = data?.emailDelivered !== false;
      showToast(
        delivered ? 'Invite re-sent' : 'Invite refreshed â€” email delivery failed',
        delivered ? 'success' : 'info',
      );
    } catch (e: any) {
      showToast(e?.response?.data?.message || e?.message || 'Failed to resend invite', 'error');
    }
  };

  const handleCopyInviteLink = async (url?: string) => {
    if (!url) {
      showToast('No link available for this invite', 'error');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      showToast('Invite link copied', 'success');
    } catch {
      showToast('Could not copy to clipboard', 'error');
    }
  };

  const handleRemoveMember = async (userId: string, name: string) => {
    if (!window.confirm(`Remove ${name} from the workspace?`)) return;
    try {
      await removeMember.mutateAsync(userId);
      showToast('Member removed', 'success');
    } catch (e: any) {
      showToast(e?.response?.data?.message || e?.message || 'Failed to remove member', 'error');
    }
  };

  const handleUpdateRole = async (userId: string, role: string) => {
    try {
      await updateMemberRole.mutateAsync({ userId, role });
      showToast('Role updated', 'success');
    } catch (e: any) {
      showToast(e?.response?.data?.message || e?.message || 'Failed to update role', 'error');
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      await cancelInvite.mutateAsync(inviteId);
      showToast('Invite cancelled', 'success');
    } catch (e: any) {
      showToast(e?.response?.data?.message || e?.message || 'Failed to cancel invite', 'error');
    }
  };

  const handleStartMfaEnrol = async () => {
    setMfaEnrolling(true);
    setMfaQr(null);
    setMfaSecret(null);
    setMfaCode('');
    setMfaFactorId(null);
    try {
      const { data, error } = await supabaseClient.auth.mfa.enroll({ factorType: 'totp' });
      if (error) throw error;
      setMfaQr(data?.totp?.qr_code || null);
      setMfaSecret(data?.totp?.secret || null);
      setMfaFactorId(data?.id || null);
    } catch (e: any) {
      showToast(e?.message || 'Failed to start 2FA setup', 'error');
      setMfaEnrolling(false);
    }
  };

  const handleVerifyMfa = async () => {
    if (!mfaFactorId || !mfaCode.trim()) {
      showToast('Enter the 6-digit code from your authenticator app', 'error');
      return;
    }
    try {
      const { data: challengeData, error: challengeError } = await supabaseClient.auth.mfa.challenge({ factorId: mfaFactorId });
      if (challengeError) throw challengeError;
      const { error: verifyError } = await supabaseClient.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challengeData.id,
        code: mfaCode.trim(),
      });
      if (verifyError) throw verifyError;
      setMfaEnrolling(false);
      setMfaQr(null);
      setMfaSecret(null);
      setMfaCode('');
      setMfaFactorId(null);
      await refetchMfa();
      showToast('Two-factor authentication enabled', 'success');
    } catch (e: any) {
      showToast(e?.message || 'Invalid code â€” please try again', 'error');
    }
  };

  const handleDisableMfa = async (factorId: string) => {
    if (!window.confirm('Disable two-factor authentication? This will make your account less secure.')) return;
    try {
      await disableMfa.mutateAsync(factorId);
      await refetchMfa();
      showToast('Two-factor authentication disabled', 'success');
    } catch (e: any) {
      showToast(e?.response?.data?.message || e?.message || 'Failed to disable 2FA', 'error');
    }
  };

  const setUserField = (key: keyof NonNullable<SettingsPayload['user']>, value: any) => {
    setFormState((prev) => ({
      ...prev,
      user: {
        ...(prev.user || {}),
        [key]: value,
      },
    }));
  };

  const setCompanyField = (key: keyof NonNullable<SettingsPayload['company']>, value: any) => {
    setFormState((prev) => ({
      ...prev,
      company: {
        ...(prev.company || {}),
        [key]: value,
      },
    }));
  };

  const setSettingsField = (key: keyof NonNullable<SettingsPayload['settings']>, value: any) => {
    setFormState((prev) => ({
      ...prev,
      settings: {
        ...(prev.settings || {}),
        [key]: value,
      },
    }));
  };

  const isDirty = useMemo(() => {
    try {
      return JSON.stringify(formState) !== JSON.stringify(baselineState);
    } catch {
      return true;
    }
  }, [formState, baselineState]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            <SettingsCard
              icon={<User className="h-4 w-4" />}
              title="Profile"
              subtitle="Update your profile photo and personal details."
            >
              <div className="flex items-center gap-4">
                {profilePhotoUrl ? (
                  <img
                    src={profilePhotoUrl}
                    alt="Profile"
                    className="h-12 w-12 rounded-full object-cover border border-[#E6E6E4]"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-semibold">
                    {initials}
                  </div>
                )}
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#181818]">Profile Photo</div>
                  <div className="mt-1 text-xs text-[#686E6C]">Min 400x400px, PNG or JPEG formats</div>
                  <div className="mt-3">
                    <input
                      ref={profilePhotoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        uploadProfilePhoto.mutate(file);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => profilePhotoInputRef.current?.click()}
                      disabled={uploadProfilePhoto.isPending}
                    >
                      Upload
                    </button>
                  </div>
                </div>
              </div>
            </SettingsCard>

            <SettingsCard
              icon={<User className="h-4 w-4" />}
              title="Personal Information"
              subtitle="Keep your contact details and certifications up to date."
            >
              <div className="space-y-5">
                <FieldRow>
                  <Field label="First name">
                    <input
                      className="form-input"
                      placeholder="Enter your first name"
                      value={formState.user?.firstName || ''}
                      onChange={(e) => setUserField('firstName', e.target.value)}
                    />
                  </Field>

                  <Field label="Last name">
                    <input
                      className="form-input"
                      placeholder="Enter your last name"
                      value={formState.user?.lastName || ''}
                      onChange={(e) => setUserField('lastName', e.target.value)}
                    />
                  </Field>
                </FieldRow>

                <FieldRow>
                  <Field label="Email address">
                    <input
                      className="form-input"
                      type="email"
                      value={(settingsData as any)?.user?.email || user?.email || ''}
                      readOnly
                    />
                  </Field>

                  <Field label="Phone number">
                    <input
                      className="form-input"
                      type="tel"
                      placeholder="Enter your phone number"
                      value={formState.user?.phone || ''}
                      onChange={(e) => setUserField('phone', e.target.value)}
                    />
                  </Field>
                </FieldRow>

                <Field label="Date of birth">
                  <input
                    className="form-input"
                    type="date"
                    value={formState.user?.dateOfBirth || ''}
                    onChange={(e) => setUserField('dateOfBirth', e.target.value)}
                  />
                </Field>

                <Field label="Address">
                  <input
                    className="form-input"
                    placeholder="Enter your address"
                    value={formState.user?.address || ''}
                    onChange={(e) => setUserField('address', e.target.value)}
                  />
                </Field>

                <Field label="Gas Safe / Engineer number">
                  <input
                    className="form-input"
                    placeholder="e.g. 123456"
                    value={formState.user?.gasSafeNumber || ''}
                    onChange={(e) => setUserField('gasSafeNumber', e.target.value)}
                  />
                </Field>
              </div>
            </SettingsCard>
          </div>
        );

      case 'company':
        return (
          <div className="space-y-6">
            <SettingsCard
              icon={<Building2 className="h-4 w-4" />}
              title="Workspace Logo"
              subtitle="Upload your company logo used on documents and in the app."
            >
              <div className="flex items-center gap-4">
                {companyLogoUrl ? (
                  <img
                    src={companyLogoUrl}
                    alt="Company logo"
                    className="h-12 w-12 rounded-lg object-cover border border-[#E6E6E4]"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-lg border border-[#E6E6E4] bg-[#FEFEFE]" />
                )}
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#181818]">Company logo</div>
                  <div className="mt-1 text-xs text-[#686E6C]">JPG, PNG up to 10MB</div>
                  <div className="mt-3">
                    <input
                      ref={companyLogoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        uploadCompanyLogo.mutate(file);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => companyLogoInputRef.current?.click()}
                      disabled={uploadCompanyLogo.isPending}
                    >
                      Upload
                    </button>
                  </div>
                </div>
              </div>
            </SettingsCard>

            <SettingsCard
              icon={<Building2 className="h-4 w-4" />}
              title="Business Details"
              subtitle="Company details that appear on invoices and certificates."
            >
              <div className="space-y-5">
                <Field label="Company name">
                  <input
                    className="form-input"
                    placeholder="Company name"
                    value={formState.company?.name || ''}
                    onChange={(e) => setCompanyField('name', e.target.value)}
                  />
                </Field>

                <FieldRow>
                  <Field label="Registration number">
                    <input
                      className="form-input"
                      placeholder="Company registration number"
                      value={formState.company?.registrationNumber || ''}
                      onChange={(e) => setCompanyField('registrationNumber', e.target.value)}
                    />
                  </Field>
                  <Field label="VAT number">
                    <input
                      className="form-input"
                      placeholder="VAT number"
                      value={formState.company?.vatNumber || ''}
                      onChange={(e) => setCompanyField('vatNumber', e.target.value)}
                    />
                  </Field>
                </FieldRow>

                <Field label="VAT rate (%)">
                  <input
                    className="form-input"
                    type="number"
                    step="0.1"
                    placeholder="20"
                    value={formState.company?.vatRate ?? ''}
                    onChange={(e) =>
                      setCompanyField('vatRate', e.target.value === '' ? undefined : Number(e.target.value))
                    }
                  />
                </Field>
                <Field label="Company address">
                  <input
                    className="form-input"
                    placeholder="Company address"
                    value={formState.company?.address || ''}
                    onChange={(e) => setCompanyField('address', e.target.value)}
                  />
                </Field>

                <FieldRow>
                  <Field label="Working hours start">
                    <input
                      className="form-input"
                      type="time"
                      value={formState.company?.workingHoursStart || ''}
                      onChange={(e) => setCompanyField('workingHoursStart', e.target.value)}
                    />
                  </Field>
                  <Field label="Working hours end">
                    <input
                      className="form-input"
                      type="time"
                      value={formState.company?.workingHoursEnd || ''}
                      onChange={(e) => setCompanyField('workingHoursEnd', e.target.value)}
                    />
                  </Field>
                </FieldRow>

                <FieldRow>
                  <Field label="Business email">
                    <input
                      className="form-input"
                      type="email"
                      placeholder="billing@company.com"
                      value={formState.company?.businessEmail || ''}
                      onChange={(e) => setCompanyField('businessEmail', e.target.value)}
                    />
                  </Field>
                  <Field label="Business phone">
                    <input
                      className="form-input"
                      type="tel"
                      placeholder="+44"
                      value={formState.company?.phone || ''}
                      onChange={(e) => setCompanyField('phone', e.target.value)}
                    />
                  </Field>
                </FieldRow>

                <Field label="Gas Safe registration number">
                  <input
                    className="form-input"
                    placeholder="Gas Safe registration number"
                    value={formState.company?.gasRegistrationNumber || ''}
                    onChange={(e) => setCompanyField('gasRegistrationNumber', e.target.value)}
                  />
                </Field>
              </div>
            </SettingsCard>
          </div>
        );

      case 'team': {
        const members: any[] = Array.isArray(teamData?.members) ? teamData.members : [];
        const pendingInvites: any[] = Array.isArray(teamData?.pendingInvites) ? teamData.pendingInvites : [];
        const totalMembers = members.length;
        const admins = members.filter((m: any) => m.role === 'ADMIN').length;
        const pending = pendingInvites.length;
        const ROLE_OPTIONS = ['ADMIN', 'OFFICE', 'ENGINEER'];
        return (
          <div className="space-y-6">
            <SettingsCard
              icon={<Users className="h-4 w-4" />}
              title="Team Overview"
              subtitle="Workspace members and pending invitations."
            >
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {[
                  { label: 'Total Members', value: totalMembers },
                  { label: 'Admins', value: admins },
                  { label: 'Engineers', value: members.filter((m: any) => m.role === 'ENGINEER').length },
                  { label: 'Pending Invites', value: pending },
                ].map((kpi) => (
                  <div key={kpi.label} className="rounded-lg border border-[#E6E6E4] bg-white p-4">
                    <div className="text-xs text-[#686E6C]">{kpi.label}</div>
                    <div className="mt-2 text-lg font-semibold text-[#181818]">{kpi.value}</div>
                  </div>
                ))}
              </div>
            </SettingsCard>

            <SettingsCard
              icon={<Users className="h-4 w-4" />}
              title="Members"
              subtitle="People who currently have access to this workspace."
            >
              {teamLoading ? (
                <div className="text-sm text-[#686E6C]">Loading membersâ€¦</div>
              ) : (
                <div className="space-y-3">
                  {members.map((link: any) => {
                    const member = link.user || link;
                    const memberId = member.id || link.userId;
                    const isMe = memberId === user?.id || member.email === user?.email;
                    return (
                      <React.Fragment key={memberId}>
                      <div className="rounded-lg border border-[#E6E6E4] bg-white p-4 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {member.profilePhotoUrl ? (
                            <img src={member.profilePhotoUrl} alt="" className="h-9 w-9 rounded-full object-cover border border-[#E6E6E4] flex-shrink-0" />
                          ) : (
                            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                              {String(member.name || member.email || '?').slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-[#181818] truncate">{member.name || member.email}</div>
                            <div className="text-xs text-[#686E6C] truncate">{member.email}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <select
                            className="form-input py-1 text-xs"
                            value={link.role || 'ENGINEER'}
                            disabled={isMe}
                            onChange={(e) => handleUpdateRole(memberId, e.target.value)}
                          >
                            {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r.charAt(0) + r.slice(1).toLowerCase()}</option>)}
                          </select>
                          {(link.role === 'ENGINEER' || member.role === 'ENGINEER') && (
                            <button
                              type="button"
                              className="btn btn-ghost text-xs"
                              onClick={() => setEngineerEditorTarget({ id: memberId, ...member })}
                            >
                              Engineer profile
                            </button>
                          )}
                          {!isMe && (
                            <button
                              type="button"
                              className="btn btn-ghost text-xs text-red-600 hover:bg-red-50"
                              onClick={() => handleRemoveMember(memberId, member.name || member.email)}
                              disabled={removeMember.isPending}
                            >
                              Remove
                            </button>
                          )}
                          {isMe && <span className="text-xs text-[#686E6C]">(you)</span>}
                        </div>
                      </div>
                      {(link.role === 'ENGINEER' || member.role === 'ENGINEER') && (
                        (Array.isArray(member.skills) && member.skills.length > 0) || member.basePostcode ? (
                          <div className="col-span-full mt-0 flex flex-wrap items-center gap-1 px-4 pb-3 text-[11px] text-[#686E6C]">
                            {member.basePostcode && (
                              <span className="inline-flex items-center gap-1">
                                ðŸ“ {member.basePostcode}
                                {member.serviceRadiusMiles ? ` Â· within ${member.serviceRadiusMiles}mi` : ''}
                              </span>
                            )}
                            {Array.isArray(member.skills) && member.skills.map((s: string) => (
                              <span key={s} className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{s}</span>
                            ))}
                          </div>
                        ) : null
                      )}
                      </React.Fragment>
                    );
                  })}

                  {pendingInvites.length > 0 && (
                    <>
                      <div className="text-xs font-semibold uppercase tracking-wide text-[#686E6C] pt-2">Pending Invites</div>
                      {pendingInvites.map((inv: any) => {
                        const expired = inv.expiresAt && new Date(inv.expiresAt) < new Date();
                        return (
                          <div key={inv.id} className="rounded-lg border border-dashed border-[#E6E6E4] bg-[#FAFAFA] p-4 flex items-center justify-between gap-3 flex-wrap">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-[#181818] truncate">{inv.email}</div>
                              <div className="text-xs text-[#686E6C]">
                                Invited as {inv.role?.toLowerCase()} Â· {expired ? (<span className="text-red-600 font-medium">expired</span>) : <>expires {new Date(inv.expiresAt).toLocaleDateString()}</>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                type="button"
                                className="btn btn-ghost text-xs"
                                onClick={() => handleCopyInviteLink(inv.acceptUrl)}
                                title="Copy invite link"
                              >
                                Copy link
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost text-xs"
                                onClick={() => handleResendInvite(inv.id)}
                                disabled={resendInvite.isPending}
                              >
                                {resendInvite.isPending ? 'Sendingâ€¦' : 'Resend'}
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost text-xs text-red-600 hover:bg-red-50"
                                onClick={() => handleCancelInvite(inv.id)}
                                disabled={cancelInvite.isPending}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}

                  <div className="pt-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 shadow-sm"
                      onClick={() => setShowInviteModal(true)}
                    >
                      <Users className="h-4 w-4" /> Invite team member
                    </button>
                  </div>
                </div>
              )}
            </SettingsCard>
            {engineerEditorTarget && (
              <EngineerProfileEditor
                userId={engineerEditorTarget.id}
                initial={{
                  skills: engineerEditorTarget.skills || [],
                  baseAddress: engineerEditorTarget.baseAddress || '',
                  basePostcode: engineerEditorTarget.basePostcode || '',
                  serviceRadiusMiles: engineerEditorTarget.serviceRadiusMiles ?? null,
                  maxJobsPerDay: engineerEditorTarget.maxJobsPerDay ?? null,
                  active: engineerEditorTarget.active !== false,
                }}
                onClose={() => setEngineerEditorTarget(null)}
                onSaved={async () => {
                  await queryClient.invalidateQueries({ queryKey: ['settings', 'team'] });
                  showToast('Engineer profile updated', 'success');
                }}
              />
            )}
            <InviteMemberModal
              open={showInviteModal}
              defaultRole="ENGINEER"
              onClose={() => setShowInviteModal(false)}
              onSent={() =>
                queryClient.invalidateQueries({ queryKey: ['settings', 'team'] })
              }
              showToast={showToast}
            />
          </div>
        );
      }

      case 'privacy': {
        const mfaEnabled = !!(mfaData as any)?.enabled;
        const mfaFactors: any[] = Array.isArray((mfaData as any)?.factors)
          ? (mfaData as any).factors.filter((f: any) => f.factor_type === 'totp' && f.status === 'verified')
          : [];
        return (
          <div className="space-y-6">
            <SettingsCard
              icon={<Shield className="h-4 w-4" />}
              title="Change Password"
              subtitle="Update password for enhanced account security."
            >
              <div className="space-y-5">
                <Field label="Current Password*">
                  <input
                    className="form-input"
                    type="password"
                    placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                </Field>
                <Field label="New Password*">
                  <input
                    className="form-input"
                    type="password"
                    placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </Field>
                <Field label="Confirm New Password*">
                  <input
                    className="form-input"
                    type="password"
                    placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                </Field>
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleChangePassword}
                    disabled={changePassword.isPending}
                  >
                    Update password
                  </button>
                </div>
              </div>
            </SettingsCard>

            <SettingsCard
              icon={<Shield className="h-4 w-4" />}
              title="Two-Factor Authentication"
              subtitle="Protect your account with an authenticator app (TOTP)."
            >
              {mfaEnabled ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                    <span className="font-medium text-[#181818]">2FA is enabled</span>
                  </div>
                  {mfaFactors.map((f: any) => (
                    <div key={f.id} className="rounded-lg border border-[#E6E6E4] bg-white p-4 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-[#181818]">Authenticator App</div>
                        <div className="text-xs text-[#686E6C] mt-1">Added {f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}</div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost text-xs text-red-600"
                        onClick={() => handleDisableMfa(f.id)}
                        disabled={disableMfa.isPending}
                      >
                        Disable
                      </button>
                    </div>
                  ))}
                </div>
              ) : mfaEnrolling ? (
                <div className="space-y-4">
                  <p className="text-sm text-[#686E6C]">Scan this QR code with your authenticator app (e.g. Google Authenticator, Authy), then enter the 6-digit code below.</p>
                  {mfaQr && (
                    <div className="flex justify-center">
                      <img src={mfaQr} alt="2FA QR code" className="h-40 w-40 rounded-lg border border-[#E6E6E4]" />
                    </div>
                  )}
                  {mfaSecret && (
                    <div className="text-xs text-[#686E6C] text-center">Manual key: <span className="font-mono bg-[#F5F5F3] px-2 py-0.5 rounded select-all">{mfaSecret}</span></div>
                  )}
                  <Field label="Verification code">
                    <input
                      className="form-input"
                      placeholder="000000"
                      maxLength={6}
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                      onKeyDown={(e) => e.key === 'Enter' && handleVerifyMfa()}
                    />
                  </Field>
                  <div className="flex gap-2 justify-end">
                    <button type="button" className="btn btn-ghost" onClick={() => { setMfaEnrolling(false); setMfaQr(null); setMfaCode(''); }}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={handleVerifyMfa}>Verify &amp; Enable</button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-lg border border-[#E6E6E4] bg-white p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-[#181818]">Authenticator App (TOTP)</div>
                        <div className="text-xs text-[#686E6C] mt-1">Use Google Authenticator, Authy, or any TOTP app.</div>
                      </div>
                      <button type="button" className="btn btn-primary text-xs" onClick={handleStartMfaEnrol}>Set up</button>
                    </div>
                  </div>
                </div>
              )}
            </SettingsCard>

            <SettingsCard
              icon={<Shield className="h-4 w-4" />}
              title="Active Sessions"
              subtitle="Manage devices currently logged into your account."
            >
              <div className="space-y-3">
                <div className="rounded-lg border border-[#E6E6E4] bg-white p-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-[#181818]">Current session</div>
                    <div className="mt-1 text-xs text-[#686E6C]">This device</div>
                  </div>
                  <span className="text-xs text-[#686E6C]">Active now</span>
                </div>
              </div>
            </SettingsCard>
          </div>
        );
      }

      case 'billing':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-sm font-medium text-[#181818]">
              <CreditCard className="h-4 w-4 text-[#686E6C]" />
              Plan & Billing
            </div>
            <BillingPanel />
          </div>
        );


      case 'integrations':
        return (
          <div className="space-y-6">
            <SettingsCard
              icon={<Plug className="h-4 w-4" />}
              title="Integrations"
              subtitle="Connect external services (email, accounting, storage)."
            >
              <div className="space-y-3">
                {[
                  { name: 'Accounting', desc: 'Connect Xero/QuickBooks (coming soon).' },
                  { name: 'Storage', desc: 'Connect external storage providers (coming soon).' },
                  { name: 'Calendar', desc: 'Sync jobs to your calendar (coming soon).' },
                ].map((item) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between gap-4 rounded-lg border border-[#E6E6E4] bg-white p-4"
                  >
                    <div>
                      <div className="text-sm font-medium text-[#181818]">{item.name}</div>
                      <div className="mt-1 text-xs text-[#686E6C]">{item.desc}</div>
                    </div>
                    <button type="button" className="btn btn-ghost">Connect</button>
                  </div>
                ))}
              </div>
            </SettingsCard>
          </div>
        );

      case 'documents':
        return (
          <div className="space-y-6">
            <SettingsCard
              icon={<FileText className="h-4 w-4" />}
              title="Templates"
              subtitle="Upload templates used to generate invoices and certificates."
            >
              <div className="space-y-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-[#181818]">Invoice template</div>
                    <div className="text-xs text-[#686E6C]">{invoiceTemplateUrl ? 'Uploaded' : 'Not uploaded'}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      ref={invoiceTemplateInputRef}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        uploadInvoiceTemplate.mutate(file);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => invoiceTemplateInputRef.current?.click()}
                      disabled={uploadInvoiceTemplate.isPending}
                    >
                      Upload
                    </button>
                    {invoiceTemplateUrl ? (
                      <a href={invoiceTemplateUrl} target="_blank" rel="noreferrer" className="btn btn-ghost">
                        View
                      </a>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-[#181818]">Certificate template</div>
                    <div className="text-xs text-[#686E6C]">{certificateTemplateUrl ? 'Uploaded' : 'Not uploaded'}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      ref={certificateTemplateInputRef}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        uploadCertificateTemplate.mutate(file);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => certificateTemplateInputRef.current?.click()}
                      disabled={uploadCertificateTemplate.isPending}
                    >
                      Upload
                    </button>
                    {certificateTemplateUrl ? (
                      <a href={certificateTemplateUrl} target="_blank" rel="noreferrer" className="btn btn-ghost">
                        View
                      </a>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-[#181818]">Terms template</div>
                    <div className="text-xs text-[#686E6C]">{termsTemplateUrl ? 'Uploaded' : 'Not uploaded'}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      ref={termsTemplateInputRef}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        uploadTermsTemplate.mutate(file);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => termsTemplateInputRef.current?.click()}
                      disabled={uploadTermsTemplate.isPending}
                    >
                      Upload
                    </button>
                    {termsTemplateUrl ? (
                      <a href={termsTemplateUrl} target="_blank" rel="noreferrer" className="btn btn-ghost">
                        View
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            </SettingsCard>

            <SettingsCard
              icon={<FileText className="h-4 w-4" />}
              title="Certificate templates"
              subtitle="Upload one template per certificate type and refine sections/screens before saving."
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <FieldRow>
                      <Field label="Certificate type">
                        <select
                          className="form-select"
                          value={pdfFirstCertificateTypeCode}
                          onChange={(e) => setPdfFirstCertificateTypeCode(e.target.value)}
                        >
                          <option value="CP12">CP12 â€” Landlord Gas Safety Record (Domestic)</option>
                          <option value="CP17">CP17 â€” Non-Domestic Gas Safety Record</option>
                          <option value="CP14">CP14 â€” Domestic Gas Safety Record (Homeowner)</option>
                          <option value="CP15">CP15 â€” Commercial Catering Gas Safety Record</option>
                          <option value="CP16">CP16 â€” LPG Safety Record</option>
                          <option value="CP7">CP7 â€” Gas Warning Notice</option>
                          <option value="OTHER">Other (specify)</option>
                        </select>
                      </Field>

                      {pdfFirstCertificateTypeCode === 'OTHER' ? (
                        <Field label="Other type label">
                          <input
                            className="form-input"
                            placeholder="e.g. OFTEC, Solid Fuel, Custom"
                            value={pdfFirstCertificateTypeLabel}
                            onChange={(e) => setPdfFirstCertificateTypeLabel(e.target.value)}
                          />
                        </Field>
                      ) : null}
                    </FieldRow>

                    <div className="rounded-lg border border-[#E6E6E4] bg-[#FAFAF9] p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Template info</div>
                      <div className="mt-1 text-sm font-medium text-[#181818]">MASTER</div>
                      <div className="mt-1 text-xs text-[#686E6C]">
                        Prefix
                        <span className="mx-2">â€¢</span>
                        <span className="text-[#181818]">{String(pdfFirstCertificatePrefix || '').trim() || 'GS'}</span>
                      </div>
                    </div>

                    <Field label="PDF file">
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => setPdfFirstFile(e.target.files?.[0] || null)}
                        disabled={!canUploadNewForSelectedType}
                      />
                    </Field>

                    {!canUploadNewForSelectedType && existingTemplateForSelectedType ? (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                        A template already exists for this certificate type. Select it below to view/edit.
                      </div>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    {pdfFirstFile ? (
                      <div className="text-xs text-[#686E6C]">Selected: {pdfFirstFile.name}</div>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!pdfFirstFile || runPdfFirstExperiment.isPending || !canUploadNewForSelectedType}
                    onClick={() => {
                      if (!pdfFirstFile) return;
                      const code = pdfFirstCertificateTypeCodeBackend;
                      const label = pdfFirstCertificateTypeCode === 'OTHER' ? pdfFirstCertificateTypeLabel : undefined;
                      runPdfFirstExperiment.mutate({ file: pdfFirstFile, certificateTypeCode: code, certificateTypeLabel: label });
                    }}
                  >
                    {runPdfFirstExperiment.isPending ? 'Uploadingâ€¦' : 'Upload & infer'}
                  </button>

                  {runPdfFirstExperiment.isError ? (
                    <div className="w-full basis-full rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                      {getApiErrorMessage(runPdfFirstExperiment.error)}
                    </div>
                  ) : null}

                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={!pdfFirstCertificateTypeCodeBackend || loadLatestPdfFirstExperiment.isPending}
                    onClick={() => {
                      loadLatestPdfFirstExperiment.mutate(pdfFirstCertificateTypeCodeBackend);
                    }}
                  >
                    {loadLatestPdfFirstExperiment.isPending ? 'Loadingâ€¦' : 'Load latest'}
                  </button>

                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={!pdfFirstResult || savePdfFirstAsTemplate.isPending}
                    onClick={() => savePdfFirstAsTemplate.mutate()}
                  >
                    {savePdfFirstAsTemplate.isPending ? 'Savingâ€¦' : 'Save as template'}
                  </button>

                  {pdfFirstResult ? (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => {
                        setPdfFirstResult(null);
                        setPdfFirstSchemaDraft(null);
                        setPdfFirstExtraction(null);
                        setPdfFirstMoveTargets({});
                      }}
                    >
                      Clear
                    </button>
                  ) : null}
                </div>

                <div className="rounded-lg border border-[#E6E6E4] bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Existing uploaded templates</div>
                      <div className="mt-1 text-xs text-[#686E6C]">
                        {certificateTemplatesLoading
                          ? 'Loadingâ€¦'
                          : `${(Array.isArray(certificateTemplates) ? (certificateTemplates as any[]).length : 0)} templates`}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
                    {(Array.isArray(certificateTemplates) ? (certificateTemplates as any[]) : []).slice(0, 24).map((t: any) => {
                      const createdAt = t?.createdAt ? new Date(t.createdAt) : null;
                      const isSelectedType =
                        String(t?.certificateTypeName || '').trim() &&
                        String(t?.certificateTypeName || '').trim() === String(pdfFirstCertificateTypeName || '').trim();

                      return (
                        <button
                          key={String(t?.id || '')}
                          type="button"
                          className={`group overflow-hidden rounded-xl border text-left shadow-sm transition hover:bg-white ${
                            isSelectedType ? 'border-blue-200 bg-blue-50' : 'border-[#E6E6E4] bg-[#FAFAF9]'
                          }`}
                          onClick={() => {
                            setPdfFirstDrawerTemplate(t);
                            const nextSchema = t?.schemaJson ? JSON.parse(JSON.stringify(t.schemaJson)) : null;
                            setPdfFirstDrawerSchemaDraft(nextSchema);
                            setPdfFirstDrawerBaselineJson(JSON.stringify(nextSchema || {}));
                            setHistoryTemplateId(String(t?.id || ''));
                            setPdfFirstDrawerOpen(true);
                          }}
                        >
                          <div className="h-[140px] bg-gradient-to-b from-white to-[#F3F4F6]" />
                          <div className="p-4">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-[#181818] truncate">{String(t?.templateLabel || 'MASTER')}</div>
                              <span
                                className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                                  t?.isLocked
                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                                    : 'border-amber-200 bg-amber-50 text-amber-900'
                                }`}
                              >
                                {t?.isLocked ? 'Locked' : 'Draft'}
                              </span>
                            </div>
                            <div className="mt-1 text-xs text-[#686E6C] truncate">{String(t?.certificateTypeName || '')}</div>
                            <div className="mt-2 text-xs text-[#686E6C]">
                              {createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt.toLocaleDateString() : 'â€”'}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {pdfFirstResult ? (
                  <div ref={resultsSplitRef} className="relative rounded-lg border border-[#E6E6E4] bg-[#FAFAF9] p-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Result</div>

                    {pdfLinkLine ? (
                      <svg className="pointer-events-none absolute inset-0" style={{ zIndex: 5 }}>
                        <line
                          x1={pdfLinkLine.x1}
                          y1={pdfLinkLine.y1}
                          x2={pdfLinkLine.x2}
                          y2={pdfLinkLine.y2}
                          stroke="#2563EB"
                          strokeWidth={2}
                          opacity={0.7}
                        />
                      </svg>
                    ) : null}

                    <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-[7fr_3fr]">
                      <div className="space-y-3">
                        {Array.isArray(pdfFirstSections) && pdfFirstSections.length ? (
                          <>
                        {(pdfFirstSections as any[]).map((sec: any, idx: number) => {
                          const secTitle = String(sec?.title || sec?.id || `Section ${idx + 1}`);
                          const screens: any[] = Array.isArray(sec?.screens) ? sec.screens : [];
                          return (
                            <details
                              key={String(sec?.id || secTitle || idx)}
                              className="rounded-lg border border-[#E6E6E4] bg-white p-3"
                              open
                            >
                              <summary className="cursor-pointer select-none">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="text-sm font-semibold text-[#181818] truncate">{secTitle}</div>
                                  <div className="text-xs text-[#686E6C]">
                                    {Boolean(sec?.repeatable) ? 'Repeatable' : 'Single'}
                                    <span className="mx-2">â€¢</span>
                                    {screens.length} screens
                                  </div>
                                </div>
                              </summary>

                              {screens.length ? (
                                <div className="mt-3 space-y-2">
                                  {screens.map((scr: any, sIdx: number) => {
                                    const scrTitle = String(scr?.title || `Screen ${sIdx + 1}`);
                                    const fields: any[] = Array.isArray(scr?.fields) ? scr.fields : [];
                                    return (
                                      <details
                                        key={`${secTitle}-${scrTitle}-${sIdx}`}
                                        className="rounded-lg border border-[#E6E6E4] bg-[#FAFAF9] p-3"
                                      >
                                        <summary className="cursor-pointer select-none">
                                          <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="text-sm font-medium text-[#181818]">{scrTitle}</div>
                                            <div className="text-xs text-[#686E6C]">{fields.length} fields</div>
                                          </div>
                                        </summary>

                                        {String(scr?.description || '').trim() ? (
                                          <div className="mt-2 text-xs text-[#686E6C]">{String(scr.description)}</div>
                                        ) : null}

                                        {fields.length ? (
                                          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                                            {fields.slice(0, 120).map((f: any, fIdx: number) => (
                                              <div
                                                key={`${scrTitle}-${fIdx}`}
                                                ref={(el) => {
                                                  const name = String(f?.pdfFieldName || '').trim();
                                                  if (name) fieldItemRefs.current[name] = el;
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                className={`rounded-md border px-3 py-2 transition ${
                                                  String(f?.pdfFieldName || '').trim() &&
                                                  String(f?.pdfFieldName || '').trim() === String(pdfFirstSelectedPdfFieldName || '').trim()
                                                    ? 'border-blue-200 bg-blue-50'
                                                    : 'border-[#E6E6E4] bg-white hover:bg-[#FAFAF9]'
                                                }`}
                                                onClick={() => {
                                                  const n = String(f?.pdfFieldName || '').trim();
                                                  if (!n) return;
                                                  setPdfFirstSelectedPdfFieldName(n);
                                                }}
                                              >
                                                <div className="text-xs font-medium text-[#181818] truncate">{String(f?.label || 'Field')}</div>
                                                <div className="mt-0.5 text-[11px] text-[#686E6C]">{String(f?.fieldType || f?.type || 'text')}</div>
                                                {String(f?.pdfFieldName || '').trim() ? (
                                                  <div className="mt-0.5 text-[11px] text-[#686E6C] truncate">{String(f.pdfFieldName)}</div>
                                                ) : null}
                                                {String(f?.context || '').trim() ? (
                                                  <div className="mt-1 text-[11px] text-[#686E6C]">{String(f.context)}</div>
                                                ) : null}
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="mt-2 text-xs text-[#686E6C]">No fields.</div>
                                        )}
                                      </details>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="mt-2 text-xs text-[#686E6C]">No screens.</div>
                              )}
                            </details>
                          );
                        })}

                        {pdfFirstExtractedFields.length && pdfFirstMissingFields.length ? (
                          <details className="rounded-lg border border-amber-200 bg-amber-50 p-3" open>
                            <summary className="cursor-pointer text-sm font-semibold text-amber-900">
                              Missing fields ({Number(pdfFirstMissingFields.length)})
                            </summary>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <button type="button" className="btn btn-ghost" onClick={() => addAllMissingToUnassigned()}>
                                Add all to Unassigned
                              </button>
                            </div>
                            <div className="mt-3 space-y-2">
                              {pdfFirstMissingFields.slice(0, 120).map((mf: any, mfIdx: number) => (
                                <div key={`mf-${mfIdx}`} className="rounded-md border border-amber-200 bg-white px-3 py-2">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="min-w-0">
                                      <div className="text-xs font-medium text-[#181818] truncate">
                                        {String(mf?.displayLabelSuggested || mf?.pdfFieldName || 'Field')}
                                      </div>
                                      <div className="mt-0.5 text-[11px] text-[#686E6C] truncate">{String(mf?.pdfFieldName || '')}</div>
                                      <div className="mt-0.5 text-[11px] text-[#686E6C]">{String(mf?.fieldType || 'text')}</div>
                                    </div>
                                    <button type="button" className="btn btn-ghost" onClick={() => addToUnassigned(mf)}>
                                      Add to Unassigned
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </details>
                        ) : null}

                        {pdfFirstUnassignedFields.length ? (
                          <details className="rounded-lg border border-[#E6E6E4] bg-white p-3" open>
                            <summary className="cursor-pointer text-sm font-semibold text-[#181818]">
                              Unassigned fields ({Number(pdfFirstUnassignedFields.length)})
                            </summary>
                            <div className="mt-3 space-y-2">
                              {pdfFirstUnassignedFields.slice(0, 200).map((f: any, idx: number) => {
                                const pdfFieldName = String(f?.pdfFieldName || '').trim();
                                if (!pdfFieldName) return null;

                                const currentTarget = pdfFirstMoveTargets[pdfFieldName];
                                const defaultSectionId =
                                  String(currentTarget?.sectionId || '') || String(pdfFirstAssignableSections?.[0]?.id || '').trim();
                                const secObj = pdfFirstAssignableSections.find(
                                  (s: any) => String(s?.id || '').trim() === defaultSectionId,
                                );
                                const secScreens: any[] = Array.isArray(secObj?.screens) ? secObj.screens : [];
                                const defaultScreenIndex = Number.isFinite(Number(currentTarget?.screenIndex))
                                  ? Number(currentTarget?.screenIndex)
                                  : 0;

                                return (
                                  <div key={`${pdfFieldName}-${idx}`} className="rounded-md border border-[#E6E6E4] bg-[#FAFAF9] p-3">
                                    <div className="text-xs font-medium text-[#181818] truncate">{String(f?.label || pdfFieldName)}</div>
                                    <div className="mt-0.5 text-[11px] text-[#686E6C] truncate">{pdfFieldName}</div>

                                    <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                                      <select
                                        className="form-select"
                                        value={defaultSectionId}
                                        onChange={(e) => {
                                          const nextSectionId = e.target.value;
                                          setPdfFirstMoveTargets((prev) => ({
                                            ...prev,
                                            [pdfFieldName]: { sectionId: nextSectionId, screenIndex: 0 },
                                          }));
                                        }}
                                        disabled={!pdfFirstAssignableSections.length}
                                      >
                                        {pdfFirstAssignableSections.map((s: any) => (
                                          <option key={String(s?.id || '')} value={String(s?.id || '')}>
                                            {String(s?.title || s?.id || 'Section')}
                                          </option>
                                        ))}
                                      </select>

                                      <select
                                        className="form-select"
                                        value={String(defaultScreenIndex)}
                                        onChange={(e) => {
                                          const nextIdx = Number(e.target.value);
                                          setPdfFirstMoveTargets((prev) => ({
                                            ...prev,
                                            [pdfFieldName]: {
                                              sectionId: defaultSectionId,
                                              screenIndex: Number.isFinite(nextIdx) ? nextIdx : 0,
                                            },
                                          }));
                                        }}
                                        disabled={!pdfFirstAssignableSections.length || !secScreens.length}
                                      >
                                        {secScreens.map((scr: any, sIdx: number) => (
                                          <option key={`${defaultSectionId}-scr-${sIdx}`} value={String(sIdx)}>
                                            {String(scr?.title || `Screen ${sIdx + 1}`)}
                                          </option>
                                        ))}
                                      </select>

                                      <button
                                        type="button"
                                        className="btn btn-ghost"
                                        disabled={!defaultSectionId || !pdfFirstAssignableSections.length}
                                        onClick={() => moveFromUnassigned(pdfFieldName)}
                                      >
                                        Move
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        ) : null}

                          </>
                        ) : (
                          <div className="rounded-lg border border-[#E6E6E4] bg-white p-3 text-sm text-[#686E6C]">No sections.</div>
                        )}
                      </div>

                      <div className="rounded-lg border border-[#E6E6E4] bg-white p-3" ref={pdfPreviewRef}>
                        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7280]">PDF preview</div>
                        {String((pdfFirstExtraction as any)?.previewUrl || '').trim() ? (
                          <div className="mt-3 overflow-hidden rounded-lg border border-[#E6E6E4] bg-[#FAFAF9]">
                            <Document file={String((pdfFirstExtraction as any).previewUrl)} loading={<div className="p-3 text-xs text-[#686E6C]">Loadingâ€¦</div>}>
                              <div className="relative">
                                <Page
                                  pageNumber={Number(pdfFirstPreviewPageIndex) + 1}
                                  width={pdfPreviewWidth}
                                  renderTextLayer={false}
                                  renderAnnotationLayer={false}
                                />
                                {selectedGeom &&
                                typeof (selectedGeom as any)?.pageIndex === 'number' &&
                                Number((selectedGeom as any).pageIndex) === Number(pdfFirstPreviewPageIndex) ? (
                                  (() => {
                                    const pageMeta = pdfPreviewPages[Number(pdfFirstPreviewPageIndex)];
                                    if (!pageMeta || !Number.isFinite(pageMeta?.width) || !Number.isFinite(pageMeta?.height)) return null;
                                    const scale = pdfPreviewWidth / Number(pageMeta.width);
                                    const x = typeof (selectedGeom as any)?.x === 'number' ? Number((selectedGeom as any).x) : NaN;
                                    const y = typeof (selectedGeom as any)?.y === 'number' ? Number((selectedGeom as any).y) : NaN;
                                    const w = typeof (selectedGeom as any)?.width === 'number' ? Number((selectedGeom as any).width) : NaN;
                                    const h = typeof (selectedGeom as any)?.height === 'number' ? Number((selectedGeom as any).height) : NaN;
                                    if (![x, y, w, h].every((n) => Number.isFinite(n))) return null;
                                    const topPx = (Number(pageMeta.height) - y - h) * scale;
                                    const leftPx = x * scale;
                                    return (
                                      <div
                                        className="absolute pointer-events-none rounded-md border-2 border-blue-500"
                                        style={{
                                          top: topPx,
                                          left: leftPx,
                                          width: w * scale,
                                          height: h * scale,
                                          boxShadow: '0 0 0 2px rgba(37,99,235,0.15)',
                                        }}
                                      />
                                    );
                                  })()
                                ) : null}
                              </div>
                            </Document>
                          </div>
                        ) : (
                          <div className="mt-3 text-xs text-[#686E6C]">Upload a PDF to preview.</div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-[#E6E6E4] bg-[#FAFAF9] p-4 text-sm text-[#686E6C]">
                    Select a template, choose a PDF, and run the experiment to view the inferred structure.
                  </div>
                )}
              </div>
            </SettingsCard>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="page-container">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-sm text-[#686E6C]">Settings</div>
            <h1 className="mt-1 text-3xl font-semibold text-[#181818]">Settings</h1>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#686E6C]" />
              <input
                className="h-9 w-64 rounded-lg border border-[#E6E6E4] bg-white pl-9 pr-3 text-sm text-[#181818] placeholder:text-[#9AA0A6]"
                value={headerSearch}
                onChange={(e) => setHeaderSearch(e.target.value)}
                placeholder="Search"
              />
            </div>

            <select
              className="h-9 rounded-lg border border-[#E6E6E4] bg-white px-3 text-sm text-[#181818]"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">Status: All</option>
              <option value="Active">Status: Active</option>
              <option value="Inactive">Status: Inactive</option>
            </select>

            <button type="button" className="inline-flex h-9 items-center gap-2 rounded-lg border border-[#E6E6E4] bg-white px-3 text-sm text-[#181818]">
              <ArrowUpDown className="h-4 w-4 text-[#686E6C]" />
              Sort
            </button>
          </div>
        </div>

        <div className="mt-1">
          <div className="flex flex-wrap gap-6 border-b border-[#E6E6E4] text-sm font-medium">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setTab(tab.key)}
                  className={`relative pb-3 transition-colors ${
                    isActive ? 'text-[#181818]' : 'text-[#686E6C] hover:text-[#383838]'
                  }`}
                >
                  {tab.label}
                  {isActive && <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[#1B1B1B]" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <div className="text-sm text-[#686E6C]">Loading settingsâ€¦</div>
        ) : (
          <div className="space-y-8">{renderTabContent()}</div>
        )}
      </div>

      {isDirty && !isLoading ? (
        <div className="mt-8 flex items-center justify-end gap-3 border-t border-[#E6E6E4] pt-4">
          <button type="button" className="btn btn-ghost" onClick={handleCancel} disabled={updateSettings.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={
              updateSettings.isPending ||
              uploadProfilePhoto.isPending ||
              uploadCompanyLogo.isPending ||
              uploadInvoiceTemplate.isPending ||
              uploadCertificateTemplate.isPending ||
              uploadTermsTemplate.isPending
            }
          >
            Save changes
          </button>
        </div>
      ) : null}

      {pdfFirstDrawerOpen && pdfFirstDrawerTemplate ? (
        <>
          <div
            className="fixed inset-0"
            style={{ background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(4px)', zIndex: 100 }}
            onClick={() => setPdfFirstDrawerOpen(false)}
          />
          <div
            className="fixed right-0 top-0 h-full w-[min(48vw,760px)] min-w-[560px] overflow-y-auto border-l border-[#E6E6E4] bg-[#F7F8FA]"
            style={{ zIndex: 101 }}
          >
            <div className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-xs text-[#686E6C]">Template</div>
                  <div className="mt-1 text-2xl font-semibold text-[#181818] truncate">
                    {String(pdfFirstDrawerTemplate?.templateLabel || 'MASTER')}
                  </div>
                  <div className="mt-2 text-sm text-[#686E6C]">
                    {String(pdfFirstDrawerTemplate?.certificateTypeName || '')}
                    <span className="mx-2">â€¢</span>
                    <span className="text-[#181818]">{String(pdfFirstDrawerTemplate?.certificatePrefix || '')}</span>
                  </div>
                </div>
                <button type="button" className="btn btn-ghost" onClick={() => setPdfFirstDrawerOpen(false)}>
                  Close
                </button>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {pdfFirstDrawerTemplate?.previewUrl ? (
                  <a
                    href={String(pdfFirstDrawerTemplate.previewUrl)}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-ghost"
                  >
                    Preview PDF
                  </a>
                ) : null}
                <button type="button" className="btn btn-ghost" onClick={addDrawerScreen}>
                  Add screen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!pdfFirstDrawerDirty || updateExistingTemplate.isPending}
                  onClick={() => updateExistingTemplate.mutate()}
                >
                  Save changes
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setHistoryTemplateId(String(pdfFirstDrawerTemplate?.id || ''))}
                >
                  Schema history
                </button>
              </div>

              {String(historyTemplateId || '') === String(pdfFirstDrawerTemplate?.id || '') ? (
                <TemplateSchemaHistory templateId={String(pdfFirstDrawerTemplate?.id || '')} />
              ) : null}

              <div className="mt-6 rounded-lg border border-[#E6E6E4] bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Sections</div>
                <div className="mt-3 space-y-3">
                  {(() => {
                    const schema = pdfFirstDrawerSchemaDraft || pdfFirstDrawerTemplate?.schemaJson;
                    const screens: any[] = Array.isArray(schema?.screens) ? schema.screens : [];
                    if (!screens.length) return <div className="text-sm text-[#686E6C]">No screens found.</div>;
                    return screens.slice(0, 30).map((scr: any, sIdx: number) => {
                      const scrKey = String(scr?.key || `screen-${sIdx}`);
                      const scrTitle = String(scr?.title || scrKey || `Screen ${sIdx + 1}`);
                      const sections: any[] = Array.isArray(scr?.sections) ? scr.sections : [];
                      return (
                        <details key={scrKey} className="rounded-lg border border-[#E6E6E4] bg-[#FAFAF9] p-3">
                          <summary className="cursor-pointer select-none">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-[#181818] truncate">{scrTitle}</div>
                              <div className="text-xs text-[#686E6C]">{sections.length} sections</div>
                            </div>
                          </summary>

                          <div className="mt-3">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#6B7280]">
                              Screen title
                            </div>
                            <input
                              value={scrTitle}
                              onChange={(e) => updateDrawerScreenTitle(sIdx, e.target.value)}
                              className="mt-1 w-full rounded-md border border-[#E6E6E4] bg-white px-3 py-2 text-sm"
                            />
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-ghost"
                                disabled={sIdx === 0}
                                onClick={() => reorderDrawerScreens(sIdx, sIdx - 1)}
                              >
                                Move up
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost"
                                disabled={sIdx >= screens.length - 1}
                                onClick={() => reorderDrawerScreens(sIdx, sIdx + 1)}
                              >
                                Move down
                              </button>
                              <button type="button" className="btn btn-ghost" onClick={() => addDrawerSection(sIdx)}>
                                Add section
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => {
                                  if (window.confirm('Remove this screen?')) removeDrawerScreen(sIdx);
                                }}
                              >
                                Remove screen
                              </button>
                            </div>
                          </div>

                          {sections.length ? (
                            <div className="mt-3 space-y-2">
                              {sections.slice(0, 40).map((sec: any, secIdx: number) => {
                                const secKey = String(sec?.key || `${scrKey}-section-${secIdx}`);
                                const secTitle = String(sec?.title || secKey || `Section ${secIdx + 1}`);
                                const fields: any[] = Array.isArray(sec?.fields) ? sec.fields : [];

                                return (
                                  <details key={secKey} className="rounded-lg border border-[#E6E6E4] bg-white p-3">
                                    <summary className="cursor-pointer select-none">
                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="text-sm font-medium text-[#181818] truncate">{secTitle}</div>
                                        <div className="text-xs text-[#686E6C]">{fields.length} fields</div>
                                      </div>
                                    </summary>

                                    <div className="mt-3">
                                      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#6B7280]">
                                        Section title
                                      </div>
                                      <input
                                        value={secTitle}
                                        onChange={(e) => updateDrawerSectionTitle(sIdx, secIdx, e.target.value)}
                                        className="mt-1 w-full rounded-md border border-[#E6E6E4] bg-white px-3 py-2 text-sm"
                                      />
                                      <div className="mt-2 flex flex-wrap items-center gap-2">
                                        <button
                                          type="button"
                                          className="btn btn-ghost"
                                          disabled={secIdx === 0}
                                          onClick={() => reorderDrawerSections(sIdx, secIdx, secIdx - 1)}
                                        >
                                          Move up
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-ghost"
                                          disabled={secIdx >= sections.length - 1}
                                          onClick={() => reorderDrawerSections(sIdx, secIdx, secIdx + 1)}
                                        >
                                          Move down
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-ghost"
                                          onClick={() => {
                                            if (window.confirm('Remove this section?')) removeDrawerSection(sIdx, secIdx);
                                          }}
                                        >
                                          Remove section
                                        </button>
                                      </div>
                                    </div>

                                    {fields.length ? (
                                      <div className="mt-3 space-y-2">
                                        {fields.slice(0, 200).map((f: any, fIdx: number) => {
                                          const label = String(f?.label || '').trim();
                                          const pdfFieldName = String(f?.pdfFieldName || '').trim();
                                          const fieldKey = String(f?.fieldSignature || `${scrKey}:${secKey}:${pdfFieldName}:${fIdx}`);
                                          const fromToken = `${sIdx}:${secIdx}`;
                                          const selectedTarget = String(pdfFirstDrawerMoveTargets[fieldKey] || '');
                                          return (
                                            <div
                                              key={`${secKey}-field-${fIdx}`}
                                              className="rounded-md border border-[#E6E6E4] bg-[#FAFAF9] px-3 py-2"
                                            >
                                              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#6B7280]">
                                                Field label
                                              </div>
                                              <input
                                                value={label}
                                                onChange={(e) => updateDrawerFieldLabel(sIdx, secIdx, fIdx, e.target.value)}
                                                className="mt-1 w-full rounded-md border border-[#E6E6E4] bg-white px-3 py-2 text-sm"
                                              />
                                              {pdfFieldName ? (
                                                <div className="mt-1 text-[11px] text-[#686E6C] truncate">{pdfFieldName}</div>
                                              ) : null}

                                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                                <button
                                                  type="button"
                                                  className="btn btn-ghost"
                                                  disabled={fIdx === 0}
                                                  onClick={() => reorderDrawerFields(sIdx, secIdx, fIdx, fIdx - 1)}
                                                >
                                                  Up
                                                </button>
                                                <button
                                                  type="button"
                                                  className="btn btn-ghost"
                                                  disabled={fIdx >= fields.length - 1}
                                                  onClick={() => reorderDrawerFields(sIdx, secIdx, fIdx, fIdx + 1)}
                                                >
                                                  Down
                                                </button>

                                                <select
                                                  value={selectedTarget || fromToken}
                                                  onChange={(e) => {
                                                    const v = e.target.value;
                                                    setPdfFirstDrawerMoveTargets((prev) => ({ ...prev, [fieldKey]: v }));
                                                  }}
                                                  className="h-9 rounded-md border border-[#E6E6E4] bg-white px-2 text-sm"
                                                >
                                                  {drawerMoveOptions.map((o) => (
                                                    <option key={o.value} value={o.value}>
                                                      {o.label}
                                                    </option>
                                                  ))}
                                                </select>
                                                <button
                                                  type="button"
                                                  className="btn btn-ghost"
                                                  disabled={!selectedTarget || selectedTarget === fromToken}
                                                  onClick={() => {
                                                    const [toS, toSec] = String(selectedTarget).split(':').map((n) => parseInt(n, 10));
                                                    if (!Number.isFinite(toS) || !Number.isFinite(toSec)) return;
                                                    moveDrawerFieldTo({
                                                      fromScreenIdx: sIdx,
                                                      fromSectionIdx: secIdx,
                                                      fromFieldIdx: fIdx,
                                                      toScreenIdx: toS,
                                                      toSectionIdx: toSec,
                                                    });
                                                  }}
                                                >
                                                  Move
                                                </button>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <div className="mt-2 text-xs text-[#686E6C]">No fields.</div>
                                    )}
                                  </details>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="mt-2 text-xs text-[#686E6C]">No sections.</div>
                          )}
                        </details>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
